import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CreditCard, Upload } from 'lucide-react';
import { money, fmtDate, STATE_BADGE } from '../lib/share';

// Portal del cliente: cuotas, pagos y carga de comprobantes (Pago Móvil, Zelle, Binance)
export const ClientCredit = () => {
  const [st, setSt] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [f, setF] = useState({ sale_id: '', amount: '', method: 'pago_movil', reference: '', file_id: null });
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.getMyCredit().then((r) => { setSt(r.data); const s = r.data.sales.find((x) => x.has_plan) || r.data.sales[0]; if (s) setF((x) => ({ ...x, sale_id: x.sale_id || s.sale_id })); }).catch(() => {});
    api.getMyReceipts().then((r) => setReceipts(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!st || (!st.sales.length && !st.installments.length)) return null;
  const s = st.summary;
  const RTAG = { 'En Verificación': 'bg-blue-100 text-blue-700', 'Aprobado': 'bg-green-100 text-green-700', 'Rechazado': 'bg-red-100 text-red-700' };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    setBusy(true);
    try { const r = await api.uploadFile(fd, 'receipt'); setF((x) => ({ ...x, file_id: r.data.file_id })); toast.success('SalesPRO: Comprobante adjuntado'); }
    catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: No se pudo subir el archivo'); } finally { setBusy(false); }
  };
  const send = async () => {
    try { await api.sendClientReceipt({ ...f, amount: parseFloat(f.amount) || 0 }); toast.success('SalesPRO: Pago reportado. Será verificado por la tienda.'); setF({ ...f, amount: '', reference: '', file_id: null }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error al reportar pago'); }
  };
  return (
    <Card data-testid="client-credit"><CardContent className="p-4 space-y-3">
      <h3 className="font-heading font-bold text-[#022C22] flex items-center gap-2"><CreditCard size={18} /> Mi financiamiento</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
        <div className="bg-[#F8F9FA] rounded p-2"><p className="text-[11px] text-[#64748B]">Saldo</p><p className="font-black text-red-600">{money(s.balance)}</p></div>
        <div className="bg-[#F8F9FA] rounded p-2"><p className="text-[11px] text-[#64748B]">Pagadas</p><p className="font-black">{s.installments_paid}</p></div>
        <div className="bg-[#F8F9FA] rounded p-2"><p className="text-[11px] text-[#64748B]">Pendientes</p><p className="font-black text-amber-700">{s.installments_pending}</p></div>
        <div className="bg-[#F8F9FA] rounded p-2"><p className="text-[11px] text-[#64748B]">Próximo vencimiento</p><p className="font-black">{fmtDate(s.next_due)}</p></div>
      </div>
      <div className="max-h-56 overflow-y-auto">{st.installments.map((i) => (
        <div key={i.installment_id} className="flex justify-between text-sm border-b border-[#F1F5F9] py-1"><span>{i.invoice_number} · Cuota #{i.number} · {fmtDate(i.due_date)}</span><span className="flex gap-2 items-center">{money(i.amount)}<span className={`text-[10px] px-2 rounded-full ${STATE_BADGE[i.state]}`}>{i.state}</span></span></div>))}</div>
      <div className="border-t pt-3 space-y-2">
        <p className="font-semibold text-sm text-[#022C22]">Reportar un pago</p>
        <div className="grid sm:grid-cols-4 gap-2">
          <select value={f.sale_id} onChange={(e) => setF({ ...f, sale_id: e.target.value })} className="px-2 py-2 border rounded-md text-sm" data-testid="receipt-sale-select">
            {st.sales.map((x) => <option key={x.sale_id} value={x.sale_id}>{x.invoice_number}</option>)}
          </select>
          <select value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} className="px-2 py-2 border rounded-md text-sm" data-testid="receipt-method">
            <option value="pago_movil">Pago Móvil</option><option value="zelle">Zelle</option><option value="binance">Binance Pay</option><option value="efectivo">Efectivo</option>
          </select>
          <Input type="number" placeholder="Monto $" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="receipt-amount" />
          <Input placeholder="N° de referencia" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="receipt-reference" />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <label className="inline-flex items-center gap-1 text-sm px-3 py-2 border rounded-md cursor-pointer"><Upload size={14} /> {f.file_id ? 'Comprobante listo' : busy ? 'Subiendo…' : 'Adjuntar captura'}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="receipt-file" /></label>
          <Button onClick={send} disabled={busy} data-testid="receipt-submit">Enviar pago</Button>
        </div>
      </div>
      {receipts.length > 0 && (
        <div className="border-t pt-3 space-y-1" data-testid="client-reported-payments">
          <p className="font-semibold text-sm text-[#022C22]">Mis pagos reportados</p>
          <div className="max-h-40 overflow-y-auto">
            {receipts.map((r) => (
              <div key={r.receipt_id} className="flex justify-between items-center text-sm border-b border-[#F1F5F9] py-1" data-testid={`reported-${r.receipt_id}`}>
                <span className="text-[#64748B]">{fmtDate(r.created_at)} · {r.method} · ref. {r.reference || '-'}</span>
                <span className="flex gap-2 items-center">{money(r.amount)}<span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${RTAG[r.status] || 'bg-slate-100 text-slate-700'}`}>{r.status}</span></span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#94A3B8]">Los pagos reportados quedan <b>En Verificación</b> hasta que la tienda los apruebe. Al aprobarse se descuentan de tu deuda.</p>
        </div>
      )}
    </CardContent></Card>
  );
};

export default ClientCredit;
